import { ComponentProps, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { UserDataFragment } from "@bonliva-crm/graphql";
import { sortSelectedFirst } from "@bonliva-crm/ui";
import { UserSearchMulti } from "@consultants/consultant-profile-new/user-search/user-search-multi";
import { useLocale, useUpdateEffect } from "@hooks";
import { ControllableCollapsibleFavFilter } from "../_base/controllable-collapsible-fav-filter";
import { isFilterSelected } from "../../utils/is-filter-selected";
import { FilterWithFavouriteProps } from "../../utils/filters-record-types";

export type ResponsiblePersonFilterProps = {
  label: string;
  options: UserDataFragment[];
  prependUsers?: UserDataFragment[];
} & FilterWithFavouriteProps;

export const UserSelectFilter = (props: ResponsiblePersonFilterProps) => {
  const selectRef = useRef(null);
  const { locale } = useLocale();

  const options = props.options ?? [];

  const { setValue } = useFormContext();
  const { field } = useController({ name: props.name });

  const [expanded, setExpanded] = useState(field.value?.length > 0);

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const value = options.filter((option) => field.value?.includes(option._id));

  const onChange = (value: UserDataFragment[]) => {
    setValue(
      props.name,
      value.map((user) => user._id),
      { shouldDirty: true }
    );
  };

  const isDirty = field.value && field.value.length > 0;

  const commonProps: ComponentProps<typeof UserSearchMulti> = {
    ignoreMe: false,
    groupedByTeam: false,
    value: value,
    prependUsers: props.prependUsers,
    onChange: onChange,
    selectProps: {
      name: props.name,
      hideSelectedOptions: false,
      closeMenuOnSelect: false,
      optionType: "checkbox",
      placeholder: props.label,
      showBadge: isFilterSelected(field?.value),
      isSearchable: true,
      showOptionDivider: false,
      maxMenuHeight: 600,
      hidePlaceholderOnFocus: false,
      controlShouldRenderValue: false,
      getOptionValue: (option) => option.value._id,
      isOptionSelected: (option) => field.value?.includes(option.value._id),
      sortOptionsOnMenuOpenFn: (options) =>
        sortSelectedFirst({
          options,
          value,
          valueOptionEqualityFn: (value, option) => value._id === option.value._id
        }),
      isClearable: isFilterSelected(field?.value),
      onClear: () => setValue(props.name, []),
      onBlur: () => field.onBlur()
    }
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={props.label}
        isDirty={isDirty}
        isClearable={isDirty}
        isExpanded={expanded}
        onClear={() => setValue(props.name, [])}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <UserSearchMulti
          {...commonProps}
          selectProps={{
            ...commonProps.selectProps,
            selectRef: selectRef,
            isClearable: false,
            autoFocus: false,
            openMenuOnFocus: true,
            showBadge: false,
            menuPortalTarget: document.body,
            controlShouldRenderValue: true,
            placeholder: locale.SelectValue,
            menuPlacement: "auto",
            onMenuClose: () => {
              setExpanded(field.value?.length > 0);
            },
            styleProps: {
              control: { showBorder: false },
              menu: { showBorder: true },
              valueContainer: { padding: 0 },
              menuPortal: { zIndex: 9999 }
            }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <UserSearchMulti
      {...commonProps}
      selectProps={{
        ...commonProps.selectProps,
        styleProps: { control: { showBorder: true }, menu: { showBorder: true } }
      }}
    />
  );
};
